/**
 * Material 3 Expressive Loading Indicators
 * Implements the new loading patterns introduced in M3 Expressive
 * Optimized for Bona Pisca with marine-inspired animations
 */

import React, { useEffect, useMemo } from 'react';
import { motion, useAnimation, Variants } from 'framer-motion';
import { useColors } from '../../contexts/ColorContext';
import { Material3Shape, getShapeValue } from '../../constants/material3Shapes';
import clsx from 'clsx';

// Loading indicator types from M3 Expressive
type LoadingType = 'circular' | 'linear' | 'skeleton' | 'wave' | 'pulse' | 'coral' | 'tide';
type LoadingSize = 'sm' | 'md' | 'lg' | 'xl';

interface Material3LoadingProps {
  type?: LoadingType;
  size?: LoadingSize;
  color?: 'primary' | 'secondary' | 'tertiary' | 'surface';
  message?: string;
  progress?: number; // 0-100 for determinant loading
  shape?: Material3Shape;
  marineTheme?: boolean;
  className?: string;
}

// Animation variants for different loading types
const loadingVariants: Record<LoadingType, Variants> = {
  circular: {
    spin: {
      rotate: 360,
      transition: {
        duration: 1,
        repeat: Infinity,
        ease: "linear",
      },
    },
  },
  linear: {
    flow: {
      x: [-100, 100],
      transition: {
        duration: 1.5,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  },
  skeleton: {
    shimmer: {
      x: [-100, 100],
      opacity: [0.5, 1, 0.5],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  },
  wave: {
    undulate: {
      y: [0, -8, 0],
      transition: {
        duration: 1.2,
        repeat: Infinity,
        ease: "easeInOut",
        staggerChildren: 0.1,
      },
    },
  },
  pulse: {
    heartbeat: {
      scale: [1, 1.1, 1],
      opacity: [0.7, 1, 0.7],
      transition: {
        duration: 1.5,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  },
  coral: {
    growth: {
      scale: [0.8, 1.2, 0.8],
      rotate: [0, 180, 360],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  },
  tide: {
    ebb: {
      scaleX: [1, 0.5, 1],
      scaleY: [0.5, 1, 0.5],
      transition: {
        duration: 2.5,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  },
};

// Size configurations
const sizeConfigs = {
  sm: { width: '16px', height: '16px', strokeWidth: 2 },
  md: { width: '24px', height: '24px', strokeWidth: 2.5 },
  lg: { width: '32px', height: '32px', strokeWidth: 3 },
  xl: { width: '48px', height: '48px', strokeWidth: 4 },
};

export const Material3LoadingIndicator: React.FC<Material3LoadingProps> = ({
  type = 'circular',
  size = 'md',
  color = 'primary',
  message,
  progress,
  shape = 'medium',
  marineTheme = true,
  className,
}) => {
  const { dynamicColors, mediterraneanColors } = useColors();
  const controls = useAnimation();
  
  const sizeConfig = sizeConfigs[size];
  const borderRadius = getShapeValue(shape);

  // Get color based on theme
  const getLoadingColor = () => {
    if (marineTheme) {
      switch (color) {
        case 'primary': return mediterraneanColors.bluePrimary;
        case 'secondary': return mediterraneanColors.greenSuccess;
        case 'tertiary': return mediterraneanColors.goldAccent;
        case 'surface': return dynamicColors.onSurfaceVariant;
        default: return mediterraneanColors.bluePrimary;
      }
    }
    
    switch (color) {
      case 'primary': return dynamicColors.primary;
      case 'secondary': return dynamicColors.secondary;
      case 'tertiary': return dynamicColors.tertiary;
      case 'surface': return dynamicColors.onSurfaceVariant;
      default: return dynamicColors.primary;
    }
  };

  const loadingColor = getLoadingColor();

  // Start animation
  useEffect(() => {
    const variant = loadingVariants[type];
    const firstVariantKey = Object.keys(variant)[0];
    controls.start(firstVariantKey);
  }, [type, controls]);

  // Circular Progress Indicator
  const CircularLoader = () => {
    const circumference = 2 * Math.PI * 10; // radius = 10
    const strokeDashoffset = progress !== undefined 
      ? circumference - (progress / 100) * circumference 
      : circumference * 0.25;

    return (
      <motion.div
        animate={progress === undefined ? "spin" : undefined}
        variants={loadingVariants.circular}
        style={{ width: sizeConfig.width, height: sizeConfig.height }}
      >
        <svg width="100%" height="100%" viewBox="0 0 24 24">
          <circle
            cx="12"
            cy="12"
            r="10"
            fill="none"
            stroke={`${loadingColor}20`}
            strokeWidth={sizeConfig.strokeWidth}
          />
          <motion.circle
            cx="12"
            cy="12"
            r="10"
            fill="none"
            stroke={loadingColor}
            strokeWidth={sizeConfig.strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            initial={{ strokeDashoffset: circumference }}
            animate={{ 
              strokeDashoffset: progress !== undefined ? strokeDashoffset : [circumference, circumference * 0.25]
            }}
            transition={{ 
              duration: progress !== undefined ? 0.5 : 2,
              repeat: progress !== undefined ? 0 : Infinity,
              ease: "easeInOut"
            }}
          />
        </svg>
      </motion.div>
    );
  };

  // Linear Progress Indicator
  const LinearLoader = () => (
    <div 
      className="relative overflow-hidden"
      style={{ 
        width: '100%', 
        height: '4px', 
        backgroundColor: `${loadingColor}20`,
        borderRadius: borderRadius
      }}
    >
      <motion.div
        variants={loadingVariants.linear}
        animate="flow"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
          width: progress !== undefined ? `${progress}%` : '30%',
          backgroundColor: loadingColor,
          borderRadius: borderRadius,
        }}
      />
    </div>
  );

  // Skeleton Loader (New in M3 Expressive)
  const SkeletonLoader = () => (
    <motion.div
      variants={loadingVariants.skeleton}
      animate="shimmer"
      className="relative overflow-hidden"
      style={{
        width: '100%',
        height: sizeConfig.height,
        backgroundColor: `${dynamicColors.onSurfaceVariant}10`,
        borderRadius: borderRadius,
      }}
    >
      <motion.div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(90deg, transparent, ${dynamicColors.onSurfaceVariant}20, transparent)`,
          borderRadius: borderRadius,
        }}
        animate={{
          x: ['-100%', '100%'],
        }}
        transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
    </motion.div>
  );

  // Wave Loader (Marine-inspired)
  const WaveLoader = () => (
    <div className="flex items-end gap-1" style={{ height: sizeConfig.height }}>
      {[0, 1, 2, 3, 4].map((index) => (
        <motion.div
          key={index}
          variants={loadingVariants.wave}
          animate="undulate"
          style={{
            width: '3px',
            height: '60%',
            backgroundColor: loadingColor,
            borderRadius: '2px',
          }}
          transition={{
            delay: index * 0.1,
            duration: 1.2,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );

  // Pulse Loader
  const PulseLoader = () => (
    <motion.div
      variants={loadingVariants.pulse}
      animate="heartbeat"
      style={{
        width: sizeConfig.width,
        height: sizeConfig.height,
        backgroundColor: loadingColor,
        borderRadius: borderRadius,
      }}
    />
  );

  // Coral Loader (Mediterranean-inspired)
  const CoralLoader = () => (
    <motion.div
      variants={loadingVariants.coral}
      animate="growth"
      style={{
        width: sizeConfig.width,
        height: sizeConfig.height,
        border: `${sizeConfig.strokeWidth}px solid ${loadingColor}`,
        borderRadius: '50%',
        position: 'relative',
      }}
    >
      <motion.div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '6px',
          height: '6px',
          backgroundColor: mediterraneanColors.coralWarning,
          borderRadius: '50%',
          transform: 'translate(-50%, -50%)',
        }}
        animate={{
          scale: [0.8, 1.2, 0.8],
        }}
        transition={{
          duration: 1,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
    </motion.div>
  );

  // Tide Loader (Mediterranean-inspired)
  const TideLoader = () => (
    <motion.div
      variants={loadingVariants.tide}
      animate="ebb"
      style={{
        width: sizeConfig.width,
        height: sizeConfig.height,
        background: `linear-gradient(45deg, ${mediterraneanColors.bluePrimary}, ${mediterraneanColors.greenSuccess})`,
        borderRadius: borderRadius,
      }}
    />
  );

  // Render appropriate loader
  const renderLoader = () => {
    switch (type) {
      case 'circular': return <CircularLoader />;
      case 'linear': return <LinearLoader />;
      case 'skeleton': return <SkeletonLoader />;
      case 'wave': return <WaveLoader />;
      case 'pulse': return <PulseLoader />;
      case 'coral': return <CoralLoader />;
      case 'tide': return <TideLoader />;
      default: return <CircularLoader />;
    }
  };

  return (
    <div className={clsx('flex flex-col items-center gap-2', className)}>
      {renderLoader()}
      {message && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-sm font-medium"
          style={{ color: dynamicColors.onSurfaceVariant }}
        >
          {message}
        </motion.p>
      )}
    </div>
  );
};

// Loading Overlay Component
export const Material3LoadingOverlay: React.FC<{
  isLoading: boolean;
  message?: string;
  type?: LoadingType;
  backdrop?: boolean;
}> = ({ 
  isLoading, 
  message = "Loading...", 
  type = 'circular',
  backdrop = true 
}) => {
  const { dynamicColors } = useColors();

  if (!isLoading) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{
        backgroundColor: backdrop ? `${dynamicColors.surface}90` : 'transparent',
        backdropFilter: backdrop ? 'blur(4px)' : 'none',
      }}
    >
      <div className="flex flex-col items-center gap-4 p-6">
        <Material3LoadingIndicator
          type={type}
          size="lg"
          message={message}
          marineTheme={true}
        />
      </div>
    </motion.div>
  );
};

export default Material3LoadingIndicator;